import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  createRetailerApi,
  getActiveBanksApi,
  getRetailersApi,
  updateRetailerAllDetailsApi,
} from "../api/api";

interface Retailer {
  retailer_id_pk: number;
  fullname: string;
  email: string;
  mobile_no: string;
  address: string;
  shop_name: string;
  shop_address: string;
  login_id_fk?: number;
  admin_id_fk?: number | null;
  record_date?: string;
  update_date?: string;
  charges?: {
    data?: Array<{
      retailer_charges_id_pk?: number;
      retailer_id_fk?: number;
      bank_id_fk?: number;
      charges_amount?: number;
      bank?: {
        bank_id?: number;
        bank_name?: string;
      };
    }>;
  };
  creator?: {
    login_id_pk: number;
    name: string;
    email: string;
  };
}

interface RetailerFormState {
  fullname: string;
  email: string;
  password: string;
  mobile_no: string;
  shop_name: string;
  address: string;
  shop_address: string;
  user_type: string;
}

interface ChargeRow {
  bankId: string;
  charges_amount: string;
  fixed_amount: string;
  per_amount: string;
  charge_type: "fixed" | "percentage";
}

interface BankOption {
  bank_id: number;
  bank_name: string;
  ifsc_code?: string;
}

const getAdminIdFromStorage = (): number | null => {
  const sessionUser = sessionStorage.getItem("user");
  if (sessionUser) {
    try {
      const parsed = JSON.parse(sessionUser);
      return Number(parsed?.id) || null;
    } catch {
      // ignore
    }
  }

  const localUser = localStorage.getItem("user");
  if (localUser) {
    try {
      const parsed = JSON.parse(localUser);
      return Number(parsed?.id) || null;
    } catch {
      // ignore
    }
  }

  return null;
};

export default function RegisterRetailerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [pagination, setPagination] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    has_more: boolean;
  } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState<Retailer | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [form, setForm] = useState<RetailerFormState>({
    fullname: "",
    email: "",
    password: "",
    mobile_no: "",
    shop_name: "",
    address: "",
    shop_address: "",
    user_type: "retailer",
  });
  const [charges, setCharges] = useState<ChargeRow[]>([
    { bankId: "", charges_amount: "", fixed_amount: "", per_amount: "", charge_type: "fixed" },
  ]);
  const [bankOptions, setBankOptions] = useState<BankOption[]>([]);
  const adminId = getAdminIdFromStorage();

  const fetchRetailers = async () => {
    setLoading(true);
    setError("");

    if (!user?.token) {
      setLoading(false);
      return;
    }

    try {
      const body = await getRetailersApi(
        { page, per_page: perPage, search },
        user.token
      );
      const list: Retailer[] = (body?.data?.data || []).map((item: any) => ({
        retailer_id_pk: item?.retailer_id ?? item?.retailer_id_pk,
        fullname: item?.fullname ?? "",
        email: item?.email ?? "",
        mobile_no: item?.mobile_no ?? "",
        address: item?.address ?? "",
        shop_name: item?.shop_name ?? "",
        shop_address: item?.shop_address ?? "",
        login_id_fk: item?.login_id ?? item?.login_id_fk,
        admin_id_fk: item?.admin_id ?? item?.admin_id_fk,
        record_date: item?.record_date,
        update_date: item?.update_date,
        charges: item?.charges,
        creator: item?.creator,
      }));
      setRetailers(list);
      setPagination(body?.data?.pagination ?? null);
    } catch (err: any) {
      setError(err.message || "Unable to load retailers");
      setRetailers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveBanks = async () => {
    if (!user?.token) return;

    try {
      const body = await getActiveBanksApi(user.token);
      const list = Array.isArray(body?.data) ? body.data : [];
      setBankOptions(list);
    } catch (err) {
      console.error("Failed to load active banks", err);
      setBankOptions([]);
    }
  };

  useEffect(() => {
    if (user?.token) {
      fetchRetailers();
      fetchActiveBanks();
    }
  }, [user?.token, page, perPage, search]);

  const addChargeRow = () => {
    setCharges([...charges, { bankId: "", charges_amount: "", fixed_amount: "", per_amount: "", charge_type: "fixed" }]);
  };

  const updateCharge = (
    index: number,
    field: keyof ChargeRow,
    value: string
  ) => {
    setCharges((current) =>
      current.map((row, idx) =>
        idx === index ? { ...row, [field]: value } : row
      )
    );
  };

  const removeCharge = (index: number) => {
    setCharges((current) =>
      current.filter((_, idx) => idx !== index)
    );
  };

  const resetForm = () => {
    setForm({
      fullname: "",
      email: "",
      password: "",
      mobile_no: "",
      shop_name: "",
      address: "",
      shop_address: "",
      user_type: "retailer",
    });
    setCharges([{ bankId: "", charges_amount: "", fixed_amount: "", per_amount: "", charge_type: "fixed" }]);
  };

  const openEditModal = async (retailer: Retailer) => {
    setEditingRetailer(retailer);
    setShowEditModal(true);
    setError("");
    setLoadingProfile(true);
    setForm({
      fullname: retailer.fullname,
      email: retailer.email,
      password: "",
      mobile_no: retailer.mobile_no,
      shop_name: retailer.shop_name,
      address: retailer.address,
      shop_address: retailer.shop_address,
      user_type: "retailer",
    });
    setCharges([{ bankId: "", charges_amount: "", fixed_amount: "", per_amount: "", charge_type: "fixed" }]);

    if (!user?.token || !retailer.login_id_fk) {
      setLoadingProfile(false);
      return;
    }

    try {
      setForm({
        fullname: retailer.fullname,
        email: retailer.email,
        password: "",
        mobile_no: retailer.mobile_no,
        shop_name: retailer.shop_name,
        address: retailer.address,
        shop_address: retailer.shop_address,
        user_type: "retailer",
      });

      const chargeRows = retailer?.charges?.data ?? [];
      if (chargeRows.length > 0) {
        setCharges(
          chargeRows.map((charge: any) => ({
            bankId: String(charge?.bank_id_fk ?? charge?.bank_id ?? charge?.bank?.bank_id ?? ""),
            charges_amount: String(charge?.charges_amount ?? ""),
            fixed_amount: String(charge?.fixed_amount ?? ""),
            per_amount: String(charge?.per_amount ?? ""),
            charge_type: charge?.charge_type === "percentage" ? "percentage" : "fixed",
          }))
        );
      } else {
        setCharges([{ bankId: "", charges_amount: "", fixed_amount: "", per_amount: "", charge_type: "fixed" }]);
      }
    } catch (err: any) {
      console.error("Failed to load retailer charges", err);
      setError(err.message || "Unable to load retailer details");
    } finally {
      setLoadingProfile(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingRetailer(null);
    setLoadingProfile(false);
    resetForm();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    console.log("=== FORM SUBMISSION STARTED ===");
    console.log("User token available:", !!user?.token);
    console.log("Admin ID:", adminId);
    console.log("Form data:", form);
    console.log("Charges data:", charges);

    if (!user?.token) {
      setError("Not authenticated");
      console.error("No user token");
      return;
    }

    if (!adminId) {
      setError("Admin ID not found in session storage");
      console.error("No admin ID");
      return;
    }

    if (!form.fullname || !form.email || !form.password || !form.mobile_no || !form.shop_name) {
      setError("Please fill in all required fields.");
      console.error("Missing required fields");
      return;
    }
    console.log("✓ All basic fields filled");
    
    //fullname validation
    if (!/^[a-zA-Z\s]+$/.test(form.fullname)) {
      setError("Full name must contain letters only.");
      console.error("Invalid fullname");
      return false;
    }
    //email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      console.error("Invalid email");
      return false;
    }
    //mobile number validation
    if (!/^\d{10}$/.test(form.mobile_no)) {
      setError("Mobile number must be exactly 10 digits.");
      console.error("Invalid mobile");
      return false;
    }
    //shop name validation
    if (!/^[a-zA-Z\s]+$/.test(form.shop_name)) {
      setError("Shop name must contain letters only.");
      console.error("Invalid shop name");
      return false;
    }
    //address validation mandatory
    if (!form.address) {
      setError("Address is required.");
      console.error("Missing address");
      return false;
    }
    //shop address validation mandatory
    if (!form.shop_address) {
      setError("Shop address is required.");
      console.error("Missing shop address");
      return false;
    }
    console.log("✓ All field validations passed");
    
    const invalidCharge = charges.some(
      (charge) => !charge.bankId || !charge.charges_amount || !charge.fixed_amount || !charge.per_amount || !charge.charge_type
    );

    if (invalidCharge) {
      setError("Please complete bank, charge amount, fixed amount, per amount and charge type for each row.");
      console.error("Invalid charges found:", charges);
      return;
    }
    console.log("✓ All charges have required fields");

    const invalidFixedAmount = charges.some(
      (charge) => Number(charge.fixed_amount) < 1 || Number(charge.fixed_amount) > 1000
    );

    if (invalidFixedAmount) {
      setError("Fixed amount must be between 1 and 1000.");
      console.error("Invalid fixed amount range");
      return;
    }
    console.log("✓ Fixed amounts in valid range");

    const duplicateBank = charges
      .map((charge) => charge.bankId)
      .filter((bankId, index, arr) => bankId && arr.indexOf(bankId) !== index);

    if (duplicateBank.length > 0) {
      setError("Please choose different banks for each charge entry.");
      console.error("Duplicate banks:", duplicateBank);
      return;
    }
    console.log("✓ All validations passed, proceeding with API call");

    setSaving(true);

    try {
      const payload = {
        fullname: form.fullname,
        email: form.email,
        password: form.password,
        mobile_no: form.mobile_no,
        shop_name: form.shop_name,
        address: form.address,
        shop_address: form.shop_address,
        admin_id_fk: adminId,
        user_type: "retailer",
        charges: charges.map((charge) => ({
          bank_id_fk: Number(charge.bankId),
          charges_amount: Number(charge.charges_amount),
          fixed_amount: Number(charge.fixed_amount),
          per_amount: Number(charge.per_amount),
          charge_type: charge.charge_type,
        })),
      };

      console.log("Sending retailer payload:", payload);
      const body = await createRetailerApi(payload, user.token);
      console.log("API Response:", body);

      if (!body?.success) {
        throw new Error(body?.message || "Failed to create retailer");
      }

      await fetchRetailers();
      setShowForm(false);
      resetForm();
    } catch (err: any) {
      console.error("Error creating retailer:", err);
      setError(err.message || "Unable to create retailer");
    } finally {
      setSaving(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // const handleDeleteRetailer = async (retailerId: number) => {
  //   if (!window.confirm("Delete this retailer?")) return;

  //   try {
  //     // API not available yet; keep UI state in sync locally.
  //     setRetailers((current) => current.filter((item) => item.retailer_id_pk !== retailerId));
  //     setError("");
  //   } catch (err: any) {
  //     setError(err.message || "Unable to delete retailer");
  //   }
  // };

  const handleUpdateRetailer = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    console.log("=== UPDATE SUBMISSION STARTED ===");
    console.log("Editing retailer:", editingRetailer);

    if (!editingRetailer) {
      console.error("No retailer selected");
      return;
    }
    if (!user?.token) {
      setError("Not authenticated");
      console.error("No token");
      return;
    }

    if (!form.fullname || !form.email || !form.mobile_no || !form.shop_name) {
      setError("Please fill in all required fields.");
      console.error("Missing required fields in update");
      return;
    }
    console.log("✓ All basic fields filled for update");

    const invalidCharge = charges.some(
      (charge) => !charge.bankId || !charge.charges_amount || !charge.fixed_amount || !charge.per_amount || !charge.charge_type
    );

    if (invalidCharge) {
      setError("Please complete bank, charge amount, fixed amount, per amount and charge type for each row.");
      console.error("Invalid charges in update:", charges);
      return;
    }
    console.log("✓ All update charges valid");

    const invalidFixedAmount = charges.some(
      (charge) => Number(charge.fixed_amount) < 1 || Number(charge.fixed_amount) > 1000
    );

    if (invalidFixedAmount) {
      setError("Fixed amount must be between 1 and 1000.");
      console.error("Invalid fixed amount range in update");
      return;
    }
    console.log("✓ Fixed amounts in valid range for update");

    const duplicateBank = charges
      .map((charge) => charge.bankId)
      .filter((bankId, index, arr) => bankId && arr.indexOf(bankId) !== index);

    if (duplicateBank.length > 0) {
      setError("Please choose different banks for each charge entry.");
      console.error("Duplicate banks in update:", duplicateBank);
      return;
    }
    console.log("✓ All update validations passed, proceeding with API call");

    setSaving(true);
    try {
      const payload = {
        retailer_id: editingRetailer.retailer_id_pk,
        fullname: form.fullname,
        email: form.email,
        mobile_no: form.mobile_no,
        address: form.address,
        shop_name: form.shop_name,
        shop_address: form.shop_address,
        charges: charges.map((charge) => ({
          bank_id_fk: Number(charge.bankId),
          charges_amount: Number(charge.charges_amount),
          fixed_amount: Number(charge.fixed_amount),
          per_amount: Number(charge.per_amount),
          charge_type: charge.charge_type,
        })),
        mpin: "1234",
      };

      console.log("Sending update retailer payload:", payload);
      const body = await updateRetailerAllDetailsApi(payload, user.token);
      console.log("Update API Response:", body);

      if (!body?.success) {
        throw new Error(body?.message || "Failed to update retailer");
      }

      await fetchRetailers();
      closeEditModal();
    } catch (err: any) {
      console.error("Error updating retailer:", err);
      setError(err.message || "Unable to update retailer");
    } finally {
      setSaving(false);
    }
  };

  const totalCount = pagination?.total ?? retailers.length;

  return (
    <div>
      <div className="page-header flex justify-between items-center">
        <div>
          <h1>Retailers</h1>
          <p>Manage your registered retailers</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Hide Form" : "+ Register New Retailer"}
        </button>
      </div>

      {showForm && (
        <div className="card mb-6">
          <div className="section-title">
            <span>Register New Retailer</span>
            <button
              className="btn btn-outline"
              style={{ padding: "6px 14px", fontSize: "13px" }}
              onClick={() => setShowForm(false)}
              type="button"
            >
              Cancel
            </button>
          </div>

          {error && (
            <div className="error-msg" style={{ marginBottom: 12 }}>
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">
                  Full Name <span>*</span>
                </label>
                <div className="input-wrap">
                  <input
                    className="form-input"
                    placeholder="Enter full name"
                    value={form.fullname}
                    onChange={(e) => setForm({ ...form, fullname: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Email Address <span>*</span>
                </label>
                <div className="input-wrap">
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Password <span>*</span>
                </label>
                <div className="input-wrap" style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    className="form-input"
                    placeholder="Create password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    style={{ paddingRight: "42px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--text-muted)",
                    }}
                  >
                    {showPass ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Mobile Number <span>*</span>
                </label>
                <div className="input-wrap">
                  <input
                    className="form-input"
                    placeholder="Enter mobile number"
                    value={form.mobile_no}
                    onChange={(e) => setForm({ ...form, mobile_no: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Shop Name <span>*</span></label>
                <div className="input-wrap">
                  <input
                    className="form-input"
                    placeholder="Enter shop name"
                    value={form.shop_name}
                    onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <div className="input-wrap">
                  <input
                    className="form-input"
                    placeholder="Enter address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Shop Address</label>
                <div className="input-wrap">
                  <input
                    className="form-input"
                    placeholder="Enter shop address"
                    value={form.shop_address}
                    onChange={(e) => setForm({ ...form, shop_address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="section-title" style={{ marginTop: 16 }}>
              <span>Bank Charges</span>
              <button
                type="button"
                className="btn btn-outline"
                onClick={addChargeRow}
              >
                + Add Bank Charge
              </button>
            </div>

            {charges.map((charge, index) => (
              <div key={`charge-row-${index}`} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.9fr 0.9fr 0.9fr auto", gap: 12, alignItems: "end", marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Select Bank</label>
                  <div className="input-wrap">
                    <select
                      className="form-input"
                      value={charge.bankId}
                      onChange={(e) => updateCharge(index, "bankId", e.target.value)}
                      required
                    >
                      <option value="">Choose bank</option>
                      {bankOptions.map((bank) => (
                        <option key={bank.bank_id} value={bank.bank_id}>
                          {bank.bank_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Charge Amount</label>
                  <div className="input-wrap">
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter amount"
                     
                      value={charge.charges_amount}
                      onChange={(e) => updateCharge(index, "charges_amount", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Fixed Amount(1 to 1000)</label>
                  <div className="input-wrap">
                    <input
                      type="number"
                      className="form-input"
                      placeholder="1 to 1000"
                      min="1"
                      max="1000"
                      step="1"
                      value={charge.fixed_amount}
                      onChange={(e) => updateCharge(index, "fixed_amount", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Per Amount</label>
                  <div className="input-wrap">
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter per amount"
                      min="0"
                      step="0.01"
                      value={charge.per_amount}
                      onChange={(e) => updateCharge(index, "per_amount", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Charge Type</label>
                  <div className="input-wrap">
                    <select
                      className="form-input"
                      value={charge.charge_type}
                      onChange={(e) => updateCharge(index, "charge_type", e.target.value as "fixed" | "percentage")}
                      required
                    >
                      <option value="fixed">Fixed</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {charges.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => removeCharge(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px" }}>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Registering..." : "Register Retailer"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="section-title" style={{ flexWrap: "wrap", gap: "12px" }}>
          <span>All Retailers ({totalCount})</span>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <div className="input-wrap" style={{ width: "260px" }}>
              <input
                className="form-input"
                placeholder="Search retailers..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            <div>
              <select
                className="form-input"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={15}>15 per page</option>
                <option value={25}>25 per page</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Shop</th>
                <th>Address</th>
                <th>Shop Address</th>
                <th>Admin</th>
                <th>Creator</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "24px" }}>
                    Loading retailers...
                  </td>
                </tr>
              ) : retailers.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)" }}>
                    No retailers found.
                  </td>
                </tr>
              ) : (
                retailers.map((retailer, index) => (
                  <tr key={retailer.retailer_id_pk}>
                    <td style={{ color: "var(--text-muted)", fontWeight: 600 }}>
                      {(pagination?.current_page - 1) * pagination?.per_page + index + 1}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/retailers", {
                            state: {
                              retailerId: retailer.retailer_id_pk,
                              retailerLoginId: retailer.login_id_fk ?? retailer.retailer_id_pk,
                              retailerName: retailer.fullname,
                            },
                          })
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#2563eb",
                          cursor: "pointer",
                          fontWeight: 700,
                          padding: 0,
                          textAlign: "left",
                        }}
                      >
                        {retailer.fullname}
                      </button>
                    </td>
                    <td>{retailer.email}</td>
                    <td>{retailer.mobile_no}</td>
                    <td>{retailer.shop_name}</td>
                    <td>{retailer.address}</td>
                    <td>{retailer.shop_address}</td>
                    <td>{retailer.admin_id_fk ?? "-"}</td>
                    <td>{retailer.creator?.email || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" className="btn btn-outline" onClick={() => openEditModal(retailer)}>
                          Update
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() =>
                            navigate("/retailers", {
                              state: {
                                retailerId: retailer.retailer_id_pk,
                                retailerLoginId: retailer.login_id_fk ?? retailer.retailer_id_pk,
                                retailerName: retailer.fullname,
                              },
                            })
                          }
                        >
                          View Transactions
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, flexWrap: "wrap", gap: 12 }}>
          <div style={{ color: "var(--text-muted)" }}>
            Page {pagination?.current_page ?? page} of {pagination?.last_page ?? 1}
            {pagination ? ` — ${pagination.total} total` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btn-outline"
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-outline"
              type="button"
              disabled={loading || !pagination || page >= pagination.last_page}
              onClick={() => setPage((p) => Math.min((pagination?.last_page ?? p), p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showEditModal && editingRetailer && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 720, padding: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Update Retailer</div>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>⚠️ {error}</div>}
            <form onSubmit={handleUpdateRetailer}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-input" value={form.fullname} onChange={(e) => setForm({ ...form, fullname: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile Number</label>
                  <input className="form-input" value={form.mobile_no} onChange={(e) => setForm({ ...form, mobile_no: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Shop Name</label>
                  <input className="form-input" value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Shop Address</label>
                  <input className="form-input" value={form.shop_address} onChange={(e) => setForm({ ...form, shop_address: e.target.value })} />
                </div>
              </div>

              <div className="section-title" style={{ marginTop: 16 }}>
                <span>Bank Charges</span>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={addChargeRow}
                >
                  + Add Bank Charge
                </button>
              </div>

              {charges.map((charge, index) => (
                <div key={`charge-row-${index}`} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.9fr 0.9fr 0.9fr auto", gap: 12, alignItems: "end", marginTop: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Select Bank</label>
                    <div className="input-wrap">
                      <select
                        className="form-input"
                        value={charge.bankId}
                        onChange={(e) => updateCharge(index, "bankId", e.target.value)}
                        required
                      >
                        <option value="">Choose bank</option>
                        {bankOptions.map((bank) => (
                          <option key={bank.bank_id} value={bank.bank_id}>
                            {bank.bank_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Charge Amount</label>
                    <div className="input-wrap">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="Enter amount"
                        value={charge.charges_amount}
                        onChange={(e) => updateCharge(index, "charges_amount", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Fixed Amount(1 to 1000)</label>
                    <div className="input-wrap">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="1 to 1000"
                        min="1"
                        max="1000"
                        step="1"
                        value={charge.fixed_amount}
                        onChange={(e) => updateCharge(index, "fixed_amount", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Per Amount</label>
                    <div className="input-wrap">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="Enter per amount"
                        min="0"
                        step="0.01"
                        value={charge.per_amount}
                        onChange={(e) => updateCharge(index, "per_amount", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Charge Type</label>
                    <div className="input-wrap">
                      <select
                        className="form-input"
                        value={charge.charge_type}
                        onChange={(e) => updateCharge(index, "charge_type", e.target.value as "fixed" | "percentage")}
                        required
                      >
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    {charges.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => removeCharge(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
                <button type="button" className="btn btn-outline" onClick={closeEditModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving || loadingProfile}>
                  {saving ? "Updating..." : loadingProfile ? "Loading..." : "Update Retailer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
